import { ComponentProps, ReactNode } from "react";

import {
  Box,
  Heading,
  Inline,
  makeBox,
  Stack,
  Text,
  useTheme
} from "@seneca/senekit";

import { ReactComponent as errorIcon } from "common/assets/images/toasts/error.svg";
import { ReactComponent as infoIcon } from "common/assets/images/toasts/info.svg";
import { ReactComponent as successIcon } from "common/assets/images/toasts/success.svg";

import { ToastType } from "./consts";

type Props = {
  type: ToastType;
  children: {
    icon?: (props: ComponentProps<typeof Box>) => JSX.Element;
    title: ReactNode;
    description: ReactNode;
    button?: ReactNode;
  };
};

export default function ToastLayout({
  type,
  children: { icon: IconProp, title, description, button }
}: Props) {
  const Icon = IconProp || getIcon(type);
  const iconColor = useGetIconColor(type);

  return (
    <Inline space="xlarge" flexWrap="nowrap">
      {Icon && <Icon marginTop="xxsmall" color={iconColor} />}
      <Stack space="xlarge">
        <Stack space="large">
          <Heading level="5" tone={getTextTone(type)}>
            {title}
          </Heading>
          <Text size="medium">{description}</Text>
        </Stack>
        {button}
      </Stack>
    </Inline>
  );
}

const SuccessIcon = makeBox(successIcon);
const InfoIcon = makeBox(infoIcon);
const ErrorIcon = makeBox(errorIcon);

function getIcon(type: ToastType) {
  switch (type) {
    case "success":
      return SuccessIcon;
    case "error":
      return ErrorIcon;
    case "info":
    case "warning":
      return InfoIcon;
  }
}

function useGetIconColor(type: ToastType) {
  const theme = useTheme();

  switch (type) {
    case "info":
      return theme["primary-high-content"];
    case "success":
    case "warning":
    case "error":
      return theme[`${type}-high-content`];
    case "neutral":
      return undefined;
  }
}

function getTextTone(type: ToastType) {
  switch (type) {
    case "info":
      return "primary";
    case "error":
    case "warning":
    case "success":
    case "neutral":
      return type;
  }
}
